/**
 * QuoteComparisonDialog
 *
 * Side-by-side comparison of every quote linked to a Job Request. Solves the
 * "scroll between quotes then guess" problem — one screen, columns per quote,
 * uniform rows (line items, markups, VAT, grand total), one button per column
 * to award the winner.
 *
 * Props:
 *   jrId          — Job Request id
 *   open          — controls Dialog visibility
 *   onClose       — dismiss handler
 *   onSelected    — fired after a quote is awarded; parent should refetch JR
 *   onSelectQuote — optional (quoteId) => Promise<void>; when provided the
 *                   dialog delegates the actual API call to the parent so the
 *                   parent owns the toast and any side effects. When omitted
 *                   the dialog falls back to a direct PUT /select-quote call.
 */
import { useEffect, useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import API, { formatApiError } from "../lib/api";
import {
  Crown, House, Briefcase, CheckCircle, CurrencyGbp, Warning,
} from "@phosphor-icons/react";

const fmt = (n) => (Number.isFinite(n) ? Number(n) : 0).toLocaleString("en-GB", {
  style: "currency", currency: "GBP",
});

export default function QuoteComparisonDialog({ jrId, basePath, open, onClose, onSelected, onSelectQuote }) {
  const base = basePath || `/job-requests/${jrId}`;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selecting, setSelecting] = useState("");

  useEffect(() => {
    if (!open || !jrId) return;
    let cancelled = false;
    setLoading(true);
    API.get(`${base}/quotes-compare`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch((e) => {
        if (!cancelled) toast.error(formatApiError(e.response?.data?.detail) || "Failed to load comparison");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, jrId, base]);

  const quotes = data?.quotes || [];
  const selectedId = data?.selected_quote_id || "";

  // Compute the cheapest total-inc-VAT so we can badge that column with a
  // "Best price" flag. Ignore quotes without a proper total (legacy quick-add).
  const cheapestTotal = useMemo(() => {
    const withTotals = quotes.filter((q) => (q.total_inc_vat || q.grand_total || q.amount) > 0);
    if (!withTotals.length) return null;
    return Math.min(...withTotals.map((q) => q.total_inc_vat || q.grand_total || q.amount));
  }, [quotes]);

  // Build a union of every unique line-item description across quotes so
  // rows line up visually even when quote A has a line quote B doesn't. Uses
  // description as the key (fuzzy but the pragmatic default).
  const lineRowKeys = useMemo(() => {
    const keys = new Set();
    for (const q of quotes) {
      for (const li of (q.line_items || [])) {
        keys.add((li.description || li.item_name || "Line item").trim());
      }
    }
    return Array.from(keys);
  }, [quotes]);

  const linesByQuote = useMemo(() => {
    const m = {};
    for (const q of quotes) {
      m[q.id] = {};
      for (const li of (q.line_items || [])) {
        const key = (li.description || li.item_name || "Line item").trim();
        m[q.id][key] = li;
      }
    }
    return m;
  }, [quotes]);

  const handleSelect = async (quoteId) => {
    if (!quoteId || selecting) return;
    setSelecting(quoteId);
    try {
      if (onSelectQuote) {
        // Delegate to the parent so its handlers own the API call + toast.
        await onSelectQuote(quoteId);
      } else {
        await API.put(`${base}/select-quote/${quoteId}`);
        toast.success("Quote awarded");
      }
      onSelected?.();
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to award quote");
    } finally { setSelecting(""); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="sm:max-w-[95vw] max-w-[95vw] w-[95vw] max-h-[92vh] overflow-hidden p-0" data-testid="compare-quotes-dialog">
        <DialogHeader className="px-6 py-4 border-b border-border">
          <DialogTitle className="text-lg font-[Chivo] flex items-center gap-2">
            <Crown size={18} className="text-amber-500" />
            Compare Quotes {data?.jr_reference && <span className="text-sm text-muted-foreground font-normal">· {data.jr_reference}</span>}
          </DialogTitle>
        </DialogHeader>

        <div className="overflow-auto max-h-[calc(92vh-4rem)]">
          {loading && (
            <div className="p-8 text-center text-sm text-muted-foreground" data-testid="compare-loading">
              Loading comparison…
            </div>
          )}

          {!loading && quotes.length === 0 && (
            <div className="p-8 text-center text-sm text-muted-foreground">
              No quotes to compare yet — add supplier or in-house quotes first.
            </div>
          )}

          {!loading && quotes.length > 0 && (
            <div className="min-w-fit">
              {/* Column headers: source + vendor + award button */}
              <div className="grid gap-3 p-4 sticky top-0 bg-background/95 backdrop-blur border-b border-border z-10"
                   style={{ gridTemplateColumns: `220px repeat(${quotes.length}, minmax(240px, 1fr))` }}>
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground pt-2">Detail</div>
                {quotes.map((q) => {
                  const isSelected = q.id === selectedId;
                  const isSupplier = q.source_type === "supplier";
                  const isCheapest = cheapestTotal !== null && (q.total_inc_vat || q.grand_total || q.amount) === cheapestTotal;
                  return (
                    <div
                      key={q.id}
                      className={`border rounded-lg p-3 space-y-2 ${
                        isSelected
                          ? "border-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 ring-2 ring-emerald-300 dark:ring-emerald-800"
                          : "border-border bg-card"
                      }`}
                      data-testid={`compare-column-${q.id}`}
                    >
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <Badge className={isSupplier
                          ? "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 text-[10px]"
                          : "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 text-[10px]"}>
                          {isSupplier ? <><Briefcase size={10} className="mr-1" />Supplier</> : <><House size={10} className="mr-1" />In-House</>}
                        </Badge>
                        {isCheapest && quotes.length > 1 && (
                          <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 text-[10px]" data-testid={`compare-cheapest-${q.id}`}>
                            <CurrencyGbp size={10} className="mr-1" /> Best price
                          </Badge>
                        )}
                        {isSelected && (
                          <Badge className="bg-emerald-500 text-white text-[10px]" data-testid={`compare-selected-${q.id}`}>
                            <CheckCircle size={10} className="mr-1" weight="fill" /> Awarded
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm font-semibold truncate" title={q.vendor_name || q.vendor_company}>
                        {q.vendor_name || q.vendor_company || (isSupplier ? "Supplier" : "In-House")}
                      </p>
                      {(q.vendor_company && q.vendor_name) && (
                        <p className="text-[11px] text-muted-foreground truncate">{q.vendor_company}</p>
                      )}
                      {q.quote_reference && (
                        <p className="text-[10px] font-mono text-muted-foreground">{q.quote_reference}</p>
                      )}
                      {!q.has_line_items && (
                        <p className="text-[10px] text-amber-700 dark:text-amber-400 flex items-start gap-1" data-testid={`compare-no-lines-${q.id}`}>
                          <Warning size={10} className="mt-0.5 shrink-0" />
                          Quick-add quote — no line-item breakdown
                        </p>
                      )}
                      <Button
                        size="sm"
                        variant={isSelected ? "outline" : "default"}
                        className="w-full mt-1"
                        disabled={!!selecting || isSelected}
                        onClick={() => handleSelect(q.id)}
                        data-testid={`compare-award-${q.id}`}
                      >
                        {isSelected ? "Currently awarded" : selecting === q.id ? "Awarding…" : "Award this quote"}
                      </Button>
                    </div>
                  );
                })}
              </div>

              {/* Line-items rows */}
              {lineRowKeys.length > 0 && (
                <div className="px-4 pt-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Line Items</p>
                  <div className="space-y-1">
                    {lineRowKeys.map((key) => (
                      <div
                        key={key}
                        className="grid gap-3 py-2 border-b border-border/40 last:border-0"
                        style={{ gridTemplateColumns: `220px repeat(${quotes.length}, minmax(240px, 1fr))` }}
                        data-testid={`compare-line-${key.slice(0, 24)}`}
                      >
                        <div className="text-xs font-medium text-slate-700 dark:text-slate-200 pr-2" title={key}>
                          {key}
                        </div>
                        {quotes.map((q) => {
                          const li = linesByQuote[q.id]?.[key];
                          if (!li) {
                            return <div key={q.id} className="text-xs text-muted-foreground italic">—</div>;
                          }
                          return (
                            <div key={q.id} className="text-xs space-y-0.5">
                              <p className="text-[10px] text-muted-foreground">
                                {li.quantity || 0} × {fmt(li.unit_cost || 0)}
                                {li.unit ? <> / {li.unit}</> : null}
                              </p>
                              <p className="font-mono font-medium">{fmt(li.line_total || (li.quantity || 0) * (li.unit_cost || 0))}</p>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Totals rows */}
              <div className="px-4 pb-6 pt-4">
                <div className="grid gap-3 py-2 border-t border-border" style={{ gridTemplateColumns: `220px repeat(${quotes.length}, minmax(240px, 1fr))` }}>
                  <div className="text-xs text-slate-500">Base total (cost)</div>
                  {quotes.map((q) => <div key={q.id} className="text-xs font-mono">{fmt(q.base_total)}</div>)}
                </div>
                <div className="grid gap-3 py-2 border-b border-border/40" style={{ gridTemplateColumns: `220px repeat(${quotes.length}, minmax(240px, 1fr))` }}>
                  <div className="text-xs text-slate-500">
                    After Markup A <span className="text-[10px] font-normal">(admin)</span>
                  </div>
                  {quotes.map((q) => (
                    <div key={q.id} className="text-xs font-mono flex items-center gap-1.5">
                      {fmt(q.after_markup_a_total)}
                      {q.markup_a_pct > 0 && <span className="text-[10px] text-muted-foreground">+{q.markup_a_pct}%</span>}
                    </div>
                  ))}
                </div>
                <div className="grid gap-3 py-2 border-b border-border" style={{ gridTemplateColumns: `220px repeat(${quotes.length}, minmax(240px, 1fr))` }}>
                  <div className="text-xs font-semibold">Grand total (before VAT)</div>
                  {quotes.map((q) => (
                    <div key={q.id} className="text-sm font-mono font-semibold">
                      {fmt(q.grand_total || q.amount)}
                    </div>
                  ))}
                </div>
                {quotes.some((q) => q.vat_enabled) && (
                  <div className="grid gap-3 py-2 border-b border-border/40" style={{ gridTemplateColumns: `220px repeat(${quotes.length}, minmax(240px, 1fr))` }}>
                    <div className="text-xs text-slate-500">VAT</div>
                    {quotes.map((q) => (
                      <div key={q.id} className="text-xs font-mono">
                        {q.vat_enabled ? <>{fmt(q.vat_amount)} <span className="text-[10px] text-muted-foreground">@ {q.vat_rate}%</span></> : "—"}
                      </div>
                    ))}
                  </div>
                )}
                <div className="grid gap-3 py-3 bg-primary/5 rounded-lg px-2 mt-1" style={{ gridTemplateColumns: `220px repeat(${quotes.length}, minmax(240px, 1fr))` }}>
                  <div className="text-sm font-bold">Total inc. VAT</div>
                  {quotes.map((q) => {
                    const total = q.total_inc_vat || q.grand_total || q.amount || 0;
                    const isCheapest = cheapestTotal !== null && total === cheapestTotal;
                    return (
                      <div key={q.id} className={`text-base font-mono font-bold ${isCheapest && quotes.length > 1 ? "text-emerald-600 dark:text-emerald-400" : ""}`}
                           data-testid={`compare-total-${q.id}`}>
                        {fmt(total)}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
